// Copyright 2024 Sorama B.V.
/* eslint-disable @typescript-eslint/no-explicit-any */
// Copyright 2016 Sorama B.V.

import { computedFrom } from "aurelia-framework";

export class MediaQueries {
    public operatingSystem: OperatingSystem;
    public isMobile: boolean;
    public isLandscape: boolean;
    public isFetchCompatible: boolean;
    public currentBrowser: Browser;
    public isChromiumBrowser: boolean;

    @computedFrom("currentBrowser", "isChromiumBrowser")
    public get isSupportedBrowser(): boolean {
        return this.isChromiumBrowser || this.currentBrowser === Browser.Firefox;
    }

    constructor() {
        this.setOperatingSystem();
        this.setCurrentBrowser();

        this.isMobile =
            this.operatingSystem != OperatingSystem.Windows &&
            this.operatingSystem != OperatingSystem.MacOS &&
            this.operatingSystem != OperatingSystem.Linux &&
            this.operatingSystem != OperatingSystem.Unknown;

        this.isLandscape = this.isMobile && window.innerHeight > window.innerWidth;
        this.isFetchCompatible = !!self.fetch && (window as any).Intl && (Intl as any).v8BreakIterator; // Only chrome is supporting everything :(
        this.isChromiumBrowser = !!window.chrome;
    }

    /**
     * Determine the mobile operating system.
     * This function returns one of 'iOS', 'Android', 'Windows Phone', or 'unknown'.
     */
    private setOperatingSystem(): void {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;

        // Windows Phone must come first because its UA also contains "Android"
        if (/windows phone/i.test(userAgent)) {
            this.operatingSystem = OperatingSystem.WindowsPhone;
            return;
        }

        if (/android/i.test(userAgent)) {
            this.operatingSystem = OperatingSystem.Android;
            return;
        }

        // iOS detection from: http://stackoverflow.com/a/9039885/177710
        if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
            this.operatingSystem = OperatingSystem.iOS;
            return;
        }

        // From https://stackoverflow.com/questions/11219582/how-to-detect-my-browser-version-and-operating-system-using-javascript
        if (/Win/.test(userAgent)) {
            this.operatingSystem = OperatingSystem.Windows;
            return;
        }

        if (/Mac/.test(userAgent)) {
            this.operatingSystem = OperatingSystem.MacOS;
            return;
        }

        if (/Linux/.test(userAgent)) {
            this.operatingSystem = OperatingSystem.Linux;
            return;
        }

        this.operatingSystem = OperatingSystem.Unknown;
        return;
    }

    /**
     *   Get the operating Browser. Returns an enumarble.
     **/
    private setCurrentBrowser(): void {
        // Copied from: http://stackoverflow.com/questions/9847580/how-to-detect-safari-chrome-ie-firefox-and-opera-browser

        // Opera 8.0+
        const isOpera = (!!window.opr && !!opr.addons) || !!window.opera || navigator.userAgent.indexOf(" OPR/") >= 0;
        // Firefox 1.0+
        const isFirefox = typeof InstallTrigger !== "undefined";
        // At least Safari 3+: "[object HTMLElementConstructor]"
        const isSafari = Object.prototype.toString.call(window.HTMLElement).indexOf("Constructor") > 0;
        // Internet Explorer 6-11
        const isIE = /*@cc_on!@*/ false || !!document.documentMode;
        // Edge 20+
        const isEdge = !isIE && !!window.StyleMedia;
        // Chrome 1+
        const isChrome = !!window.chrome && !!window.chrome.webstore;
        // Blink engine detection
        const isBlink = (isChrome || isOpera) && !!window.CSS;

        if (isOpera) this.currentBrowser = Browser.Opera;
        else if (isFirefox) this.currentBrowser = Browser.Firefox;
        else if (isSafari) this.currentBrowser = Browser.Safari;
        else if (isIE) this.currentBrowser = Browser.InternetExplorer;
        else if (isChrome) this.currentBrowser = Browser.Chrome;
        else if (isBlink) this.currentBrowser = Browser.Blink;
        else this.currentBrowser = Browser.Unknown;
    }
}

export enum Browser {
    Firefox,
    Opera,
    Safari,
    InternetExplorer,
    Chrome,
    Blink,
    Unknown,
}

export enum OperatingSystem {
    Windows,
    MacOS,
    Linux,
    Android,
    iOS,
    WindowsPhone,
    Unknown,
}
